<template>
    <div class="ProfileMissingFieldsForm my-5 space-y-8 bg-gray-100 dark:bg-subtle p-5">
        <CHeading variant="3">
            <span
                >Doplň si údaje v profile, zvýšiš šance, že ťa vyberú
                <CTooltip
                    >Vyplnené údaje uložíme do tvojho profilu, aby si ich nemusel/a nabudúce vypĺňať.</CTooltip
                ></span
            >
        </CHeading>

        <div v-if="type === 'volunteer' && !onlyProfile" class="space-y-8">
            <VolunteerProfileForm
                ref="volunteerProfileForm"
                :missing-fields="missingFieldsVolunteer"
                only-missing-fields
                :model-value="volunteerProfile"
                @update:model-value="updateVolunteerProfile($event)"
            />

            <ProfileForm
                ref="userProfileForm"
                :missing-fields="missingFieldsUser"
                only-missing-fields
                :model-value="localValue"
                @update:model-value="updateProfile($event)"
            />
        </div>

        <div v-else-if="type !== 'volunteer' && !onlyProfile" class="space-y-8">
            <ProfileForm
                ref="userProfileForm"
                :missing-fields="missingFieldsUser"
                only-missing-fields
                :model-value="localValue"
                @update:model-value="updateProfile($event)"
            />

            <MentorProfileForm
                ref="mentorProfileForm"
                :missing-fields="missingFieldsMentor"
                only-missing-fields
                :model-value="mentorProfile"
                @update:model-value="updateMentorProfile($event)"
            />
        </div>

        <div v-else>
            <ProfileForm
                ref="userProfileForm"
                :missing-fields="missingFieldsUser"
                only-missing-fields
                :model-value="localValue"
                @update:model-value="updateProfile($event)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { ValidationErrors, ValidationResults } from 'last-validator'

import { type MentorProfile, type UserProfile, type VolunteerProfile } from '#shared/types/User'
import { scrollToError } from '#shared/utils'
import MentorProfileForm from '@/components/profile/MentorProfileForm.vue'
import ProfileForm from '@/components/profile/ProfileForm.vue'
import VolunteerProfileForm from '@/components/profile/VolunteerProfileForm.vue'
import CHeading from '@/components/ui/CHeading.vue'
import CTooltip from '@/components/ui/CTooltip.vue'

interface Props {
    type: string
    modelValue?: UserProfile
    missingFields: any
    onlyProfile?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
    'update:modelValue': [value: UserProfile]
}>()

const errors = ref<ValidationErrors>({})
const localValue = ref<UserProfile>({ ...props.modelValue || {} as UserProfile })
const volunteerProfile = ref<VolunteerProfile>(props.modelValue?.volunteerProfile || {} as VolunteerProfile)
const mentorProfile = ref<MentorProfile>(props.modelValue?.mentorProfile || {} as MentorProfile)

// Template refs
const userProfileForm = ref<any>(null)
const volunteerProfileForm = ref<any>(null)
const mentorProfileForm = ref<any>(null)

const missingFieldsUser = computed((): string[] => {
    return Object.keys(props.missingFields?.userProfile || {})
})

const missingFieldsVolunteer = computed((): string[] => {
    return Object.keys(props.missingFields?.volunteerProfile || {})
})

const missingFieldsMentor = computed((): string[] => {
    return Object.keys(props.missingFields?.mentorProfile || {})
})

// Watch for prop changes and sync to local values
watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        localValue.value = { ...newValue }
        volunteerProfile.value = newValue.volunteerProfile || {} as VolunteerProfile
        mentorProfile.value = newValue.mentorProfile || {} as MentorProfile
    }
}, { deep: true, immediate: true })

function updateProfile(data: UserProfile) {
    localValue.value = data
    emit('update:modelValue', data)
}

function updateVolunteerProfile(changes: {
    [key in keyof VolunteerProfile]: any
}) {
    volunteerProfile.value = { ...volunteerProfile.value, ...changes }
    const updatedValue = {
        ...localValue.value,
        volunteerProfile: volunteerProfile.value
    }
    localValue.value = updatedValue
    emit('update:modelValue', updatedValue)
}

function updateMentorProfile(changes: {
    [key in keyof MentorProfile]: any
}) {
    mentorProfile.value = { ...mentorProfile.value, ...changes }
    const updatedValue = {
        ...localValue.value,
        mentorProfile: mentorProfile.value
    }
    localValue.value = updatedValue
    emit('update:modelValue', updatedValue)
}

async function validate(): Promise<ValidationResults> {
    let result = await userProfileForm.value.validate()
    if (props.type === 'volunteer') {
        const resultVolunteer = await volunteerProfileForm.value.validate()
        result = {
            isValid: result.isValid && resultVolunteer.isValid,
            errors: { ...result.errors, ...resultVolunteer.errors }
        }
    } else if (props.type === 'mentor') {
        const resultMentor = await mentorProfileForm.value.validate()
        result = {
            isValid: result.isValid && resultMentor.isValid,
            errors: { ...result.errors, ...resultMentor.errors }
        }
    }

    errors.value = result.errors

    if (!result.isValid) {
        nextTick(() => scrollToError())
    }

    return result
}

// Expose methods to parent components
defineExpose({
    validate
})
</script>